<script setup lang="ts">
import { ref } from "vue";
import DRBtn from "./DRBtn.vue";

const props = withDefaults(
  defineProps<{
    description?: string;
    extensions?: string[];
    multiple?: boolean;
  }>(),
  {
    extensions: () => [],
    multiple: true,
  }
);

const emit = defineEmits<{
  (e: "files", files: File[]): void;
}>();

const isDragging = ref(false);
const isDraggingOverZone = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);

function handleDragEnter(event: DragEvent) {
  event.preventDefault();
  event.stopPropagation();
  isDragging.value = true;
}

function handleDragLeave(event: DragEvent) {
  event.preventDefault();
  event.stopPropagation();
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  const x = event.clientX;
  const y = event.clientY;

  if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
    isDragging.value = false;
    isDraggingOverZone.value = false;
  }
}

function handleDragOver(event: DragEvent) {
  event.preventDefault();
  event.stopPropagation();
  isDraggingOverZone.value = true;
}

function handleDrop(event: DragEvent) {
  event.preventDefault();
  event.stopPropagation();
  isDragging.value = false;
  isDraggingOverZone.value = false;
  if (event.dataTransfer) {
    handleFiles(event.dataTransfer.files);
  }
}

function handleFiles(files: FileList) {
  let validatedFiles = Array.from(files).filter((file) => {
    const extension = file.name.split(".").pop()?.toLowerCase();
    return (
      props.extensions.length === 0 ||
      props.extensions.includes(extension || "")
    );
  });

  if (!props.multiple && validatedFiles.length > 0) {
    validatedFiles = [validatedFiles[0]];
  }

  emit("files", validatedFiles);
}

function openFileDialog() {
  fileInput.value?.click();
}

const uuid = Math.random().toString();
</script>

<template>
  <div
    id="drop-zone"
    @dragover.prevent="handleDragOver"
    @dragenter.prevent="handleDragEnter"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
    :class="{ 'zone-hover': isDraggingOverZone }"
  >
    <div
      class="d-flex align-items-center justify-content-center flex-column h-100 w-100"
    >
      <div class="text-center mt-3">
        <el-icon size="4rem">
          <i class="bi-cloud-arrow-down-fill"></i>
        </el-icon>
        <p class="h4">
          {{ multiple ? $t("drag.your.files") : $t("drag.your.file") }}
        </p>
        <p v-if="description">{{ description }}</p>
      </div>

      <span class="opacify">{{ $t("or").toUpperCase() }}</span>

      <input
        ref="fileInput"
        type="file"
        class="d-none"
        :id="uuid"
        :multiple="multiple"
        :accept="extensions.map((str) => `.${str}`).join(',')"
        @change.prevent="(e) => handleFiles((e.target as HTMLInputElement).files!)"
      />

      <DRBtn
        class="my-3"
        :label="$t('click.to.select')"
        @click="openFileDialog"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/style/variables.scss";

#drop-zone {
  padding: 8px;
  border-radius: 8px;
  background-color: rgba(0, 0, 0, 0);
  transition: background-color 0.3s;
}

#drop-zone.zone-hover {
  background-color: $primary;
  color: white;
}
.opacify {
  opacity: 0.5;
}
</style>
